import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './useReduxAuth';
import { useNotification } from './useNotification';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_COMPANIES_FOR_USER_FORM_QUERY,
  GET_PARTNERS_QUERY,
  GET_SECTORS_QUERY,
  GET_FIRMS_FOR_REFERENCE_QUERY,
} from '../graphql/queries';
import {
  CREATE_FIRM_PARTNER_MUTATION,
  DELETE_FIRM_PARTNER_MUTATION,
  CREATE_FIRM_SECTOR_MUTATION,
  DELETE_FIRM_SECTOR_MUTATION,
} from '../graphql/mutations';
import { useDynamicEntity } from './useDynamicEntity';
import { useAccessibleFirmIds } from './useAccessibleFirmIds';

// `bypassRestriction: true` — kullanicinin isRestricted olsa bile tum
// yetkili firmalari (kendi sirketindeki hepsi) dondur. Ciro Ekle sayfasi
// icin: kullanici satış cirosu girerken yetkili olmadigi firmalara da
// prim ekleyebilmeli. Diger sayfalar default davranista kalir (kisitli).
export const useFirms = ({ paginated = false, bypassRestriction = false } = {}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { showNotification } = useNotification();

  const [partners, setPartners] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [referenceFirms, setReferenceFirms] = useState([]);
  const [companies, setCompanies] = useState([]);

  const isRootUser = currentUser?.role === 'ROOT';
  const { firmIds, loading: firmIdsLoading, isRestricted } = useAccessibleFirmIds();

  const customWhereCondition = useMemo(() => {
    if (isRootUser) return {};
    // bypassRestriction: yetki kısıtı yok, sadece şirket scope'unda tüm firmalar.
    if (bypassRestriction) return { company_id: { _eq: currentUser?.companyId } };
    if (isRestricted) {
      const safeIds = firmIds && firmIds.length > 0 ? firmIds : [-1];
      return { company_id: { _eq: currentUser?.companyId }, id: { _in: safeIds } };
    }
    return { company_id: { _eq: currentUser?.companyId } };
  }, [isRootUser, bypassRestriction, isRestricted, firmIds, currentUser?.companyId]);

  const firmIdsKey = bypassRestriction
    ? 'bypass'
    : (isRestricted ? (firmIds ? firmIds.join(',') : 'loading') : 'unrestricted');

  const dynamicHook = useDynamicEntity({
    entityType: 'firms',
    gqlEntityKey: 'firms',
    queryName: 'GET_FIRMS_QUERY',
    createMutation: 'CREATE_FIRM_MUTATION',
    updateMutation: 'UPDATE_FIRM_MUTATION',
    deleteMutation: 'DELETE_FIRM_MUTATION',
    mutationPattern: 'default',
    updateVariableName: 'set',
    serverPagination: paginated,
    aggregateKey: paginated ? 'firms_aggregate' : null,
    transformData: firm => {
      const partnerNames = firm.firm_partners?.length > 0 
        ? firm.firm_partners.map(fp => fp.partner?.name || 'Bilinmeyen Partner').join(', ')
        : 'Yok';
      
      const sectorNames = firm.firm_sectors?.length > 0
        ? firm.firm_sectors.map(fs => fs.sector?.name || 'Bilinmeyen Sektör').join(', ')
        : 'Yok';

      const isEstablished = Boolean(firm.is_established);
      const isPartnerReferral = Boolean(firm.partner_referral);

      const transformed = {
        ...firm,
        companyName: firm.company?.name || 'Unknown Company',
        company_name: firm.company?.name || 'Unknown Company',
        partnerNames: partnerNames,
        sectorNames: sectorNames,
        referenceCompanyName:
          firm.reference_firm?.firm_name || firm.reference_text || 'Yok',
        isEstablishedText: isEstablished ? 'Evet' : 'Hayır',
        partnerReferralText: isPartnerReferral ? 'Evet' : 'Hayır',
        referenceTypeText:
          firm.reference_type === 'existing'
            ? 'Mevcut Firma'
            : firm.reference_type === 'new'
              ? 'Yeni Referans'
              : 'Yok',
        is_established: isEstablished ? 'yes' : 'no',
        is_established_boolean: isEstablished, // Filtreleme için boolean değer
        partner_referral: isPartnerReferral ? 'yes' : 'no',
        partner_referral_boolean: isPartnerReferral, // Filtreleme için boolean değer
        partner_ids: firm.firm_partners?.map(fp => fp.partner_id) || [],
        sector_ids: firm.firm_sectors?.map(fs => fs.sector_id) || [],
      };

      return transformed;
    },
    transformFormData: formData => {
      const transformedData = {
        company_id: formData.companyId || currentUser?.companyId,
        firm_name: formData.firm_name || null,
        firm_short_name: formData.firm_short_name,
        firm_city: formData.firm_city,
        description: formData.description || null,
        luca_code: formData.luca_code || null,
        is_established: formData.is_established === 'yes',
        partner_referral: formData.partner_referral === 'yes',
        reference_type: formData.reference_type || null,
        reference_firm_id: formData.reference_firm_id || null,
        reference_text: formData.reference_text || null,
      };

      // Firma adını sadece is_established false ise null yap
      // Ama kullanıcı firma adı girdiyse kaydet
      if (!transformedData.is_established && !formData.firm_name) {
        transformedData.firm_name = null;
      }

      if (transformedData.reference_type === 'existing') {
        transformedData.reference_text = null;
      } else if (transformedData.reference_type === 'new') {
        transformedData.reference_firm_id = null;
      } else {
        transformedData.reference_firm_id = null;
        transformedData.reference_text = null;
      }

      return transformedData;
    },
    customWhereCondition,
    customDepsKey: firmIdsKey,
    onSuccess: async (operation, data, formData) => {
      if (operation === 'create' || operation === 'update') {
        const firmId = data?.id;
        if (!firmId) {
          console.error('No firm ID found in data');
          return;
        }

        if (formData?.partner_ids && Array.isArray(formData.partner_ids)) {
          if (operation === 'update') {
            const existingPartners = data.firm_partners || [];
            for (const existingPartner of existingPartners) {
              try {
                await hasuraClient.mutate(DELETE_FIRM_PARTNER_MUTATION, {
                  id: existingPartner.id,
                });
              } catch (error) {
                console.error(
                  'Error deleting existing partner relationship:',
                  error
                );
              }
            }
          }

          const uniquePartnerIds = [...new Set(formData.partner_ids)];
          for (const partnerId of uniquePartnerIds) {
            try {
              await hasuraClient.mutate(CREATE_FIRM_PARTNER_MUTATION, {
                object: {
                  firm_id: firmId,
                  partner_id: partnerId,
                },
              });
            } catch (error) {
              console.error('Error creating partner relationship:', error);
            }
          }
        }

        if (formData?.sector_ids && Array.isArray(formData.sector_ids)) {
          if (operation === 'update') {
            const existingSectors = data.firm_sectors || [];
            for (const existingSector of existingSectors) {
              try {
                await hasuraClient.mutate(DELETE_FIRM_SECTOR_MUTATION, {
                  id: existingSector.id,
                });
              } catch (error) {
                console.error(
                  'Error deleting existing sector relationship:',
                  error
                );
              }
            }
          }

          const uniqueSectorIds = [...new Set(formData.sector_ids)];
          for (const sectorId of uniqueSectorIds) {
            try {
              await hasuraClient.mutate(CREATE_FIRM_SECTOR_MUTATION, {
                object: {
                  firm_id: firmId,
                  sector_id: sectorId,
                },
              });
            } catch (error) {
              console.error('Error creating sector relationship:', error);
            }
          }
        }
      }
    },
  });

  const fetchPartners = useCallback(async () => {
    try {
      const data = await hasuraClient.query(GET_PARTNERS_QUERY, {
        where: isRootUser
          ? {}
          : { company_id: { _eq: currentUser?.companyId } },
      });

      setPartners(data.partners || []);
    } catch (error) {
      console.error('Error fetching partners:', error);
      showNotification(t('partners.messages.fetchError'), 'error');
    }
  }, [isRootUser, currentUser?.companyId, t, showNotification]);

  const fetchSectors = useCallback(async () => {
    try {
      const data = await hasuraClient.query(GET_SECTORS_QUERY, {
        where: isRootUser
          ? {}
          : { company_id: { _eq: currentUser?.companyId } },
      });
      setSectors(data.sectors || []);
    } catch (error) {
      console.error('Error fetching sectors:', error);
      showNotification(t('sectors.messages.fetchError'), 'error');
    }
  }, [isRootUser, currentUser?.companyId, t, showNotification]);

  const fetchReferenceFirms = useCallback(async () => {
    try {
      const data = await hasuraClient.query(GET_FIRMS_FOR_REFERENCE_QUERY, {
        companyId: currentUser?.companyId,
      });
      setReferenceFirms(data.firms || []);
    } catch (error) {
      console.error('Error fetching reference firms:', error);
    }
  }, [currentUser?.companyId]);

  const fetchCompanies = useCallback(async () => {
    try {
      const data = await hasuraClient.query(GET_COMPANIES_FOR_USER_FORM_QUERY);
      setCompanies(data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
    fetchSectors();
    fetchReferenceFirms();
    fetchCompanies();
  }, [fetchPartners, fetchSectors, fetchReferenceFirms, fetchCompanies]);

  return {
    ...dynamicHook,
    isLoading: dynamicHook.isLoading || (isRestricted && firmIdsLoading),
    partners,
    sectors,
    referenceFirms,
    companies,
    isRootUser,
  };
};
